import { Service, signal } from '@angular/core';

export type StatusSolicitacao =
  | 'aberto'
  | 'em-andamento'
  | 'respondido'
  | 'finalizado'
  | 'cancelado';

export type CategoriaSolicitacao = 'ouvidoria' | 'sic' | 'servico';

/** `vermelho` é só do evento de cancelamento da solicitação de serviço, que vem da API. */
export type TomHistorico = 'azul' | 'ambar' | 'verde' | 'vermelho';

export interface ArquivoSolicitacao {
  id: string;
  nome: string;
  data: string;
  tamanho: string;
  url: string;
}

export interface EventoHistorico {
  id: string;
  rotulo: string;
  tom: TomHistorico;
  quando: string;
  descricao: string;
  /** Só no e-SIC: os arquivos que vieram com o evento (a resposta ao complemento), pelo nome. */
  anexos?: readonly ArquivoEvento[];
}

/** Um arquivo citado num evento — só nome e tamanho: o portal não baixa anexo do e-SIC. */
export interface ArquivoEvento {
  nome: string;
  tamanho: string;
}

/** Um anexo da resposta do órgão. O `codigo` é o que a rota pública de download pede. */
export interface AnexoRespostaSolicitacao {
  codigo: number;
  nome: string;
  /** Tamanho legível ('2.0 KB'). */
  tamanho: string;
}

/**
 * Uma resposta à solicitação. Os campos opcionais só existem na solicitação de serviço (que
 * vem da API); os mocks de Ouvidoria e SIC têm apenas data e parágrafos.
 */
export interface RespostaSolicitacao {
  respondidaEm: string;
  paragrafos: readonly string[];
  /** O status em que a solicitação ficou com esta resposta, no vocabulário do domínio. */
  status?: string;
  links?: readonly string[];
  formularios?: readonly string[];
  anexos?: readonly AnexoRespostaSolicitacao[];
  /** Só no e-SIC: o tipo da resposta da LAI (*Acesso concedido*…). */
  tipo?: string;
  /** Só no e-SIC: o fundamento legal, quando a resposta nega ou concede em parte. */
  fundamento?: string;
}

export interface Solicitacao {
  protocolo: string;
  servico: string;
  descricao: string;
  endereco: string;
  abertaEm: string;
  abertaAs: string;
  status: StatusSolicitacao;
  categoria: CategoriaSolicitacao;
  tipo: string;
  prazo: string;
  arquivos: readonly ArquivoSolicitacao[];
  historico: readonly EventoHistorico[];
  /** O parecer único dos mocks de Ouvidoria e SIC. */
  resposta?: RespostaSolicitacao;
  /**
   * As respostas do órgão na solicitação de serviço, da mais antiga para a mais recente.
   * Quando há alguma, o detalhe as mostra no lugar do `resposta`.
   */
  respostas?: readonly RespostaSolicitacao[];
  motivoCancelamento?: string;
  /** Só na solicitação de serviço: o pedido é de reemissão de documento já emitido. */
  segundaVia?: boolean;
  /** O complemento que o órgão pediu e ainda espera; ausente quando não há. */
  pendencia?: PendenciaSolicitacao;
  /**
   * Só no pedido do e-SIC (categoria `sic`): o que a Carta de Serviço não tem — o assunto, o
   * rótulo do status do backend, o complemento com as regras de anexo e a situação de recurso.
   */
  sic?: DadosPedidoSic;
}

/** O que o pedido do e-SIC traz a mais, já no vocabulário da tela. */
export interface DadosPedidoSic {
  assunto: string;
  /** O rótulo do backend, um dos dez — é o que o selo mostra. O `status` é o grupo do filtro. */
  statusDescricao: string;
  /** A categoria temática. */
  departamento: string;
  complemento: ComplementoPedidoSic | null;
  sujeitoARecurso: boolean;
  /** `dd/mm/aaaa`; vazio quando não está sujeito a recurso. */
  dataLimiteRecurso: string;
  justificativaObrigatoria: boolean;
}

/** O complemento pendente do e-SIC, com as regras de anexo do departamento atual. */
export interface ComplementoPedidoSic {
  solicitacao: string;
  /** `dd/mm/aaaa`. */
  dataLimite: string;
  aceitaResposta: boolean;
  permiteAnexos: boolean;
  quantidadeMaxima: number;
  tamanhoMaximoMb: number;
  /** Os rótulos (`PDF`, `JPG`) para a ajuda do campo. */
  formatos: readonly string[];
  /** As extensões aceitas, sem ponto e em minúsculas. */
  extensoes: readonly string[];
}

/** O pedido de complemento do órgão, pronto para a tela. */
export interface PendenciaSolicitacao {
  mensagem: string;
  /** `dd/mm/aaaa - HH:mm`. */
  pedidaEm: string;
  /** Até quando enviar; vazio quando o pedido não pausa o prazo do órgão. */
  limite: string;
  pausaPrazo: boolean;
}

export interface CategoriaOpcao {
  id: CategoriaSolicitacao;
  rotulo: string;
  icone: string;
}

export const CATEGORIAS_SOLICITACAO: readonly CategoriaOpcao[] = [
  { id: 'ouvidoria', rotulo: 'Ouvidoria', icone: 'megaphone' },
  { id: 'sic', rotulo: 'SIC', icone: 'file-search' },
  { id: 'servico', rotulo: 'Serviço', icone: 'clipboard-list' },
];

export interface StatusOpcao {
  id: StatusSolicitacao;
  rotulo: string;
  icone: string;
}

export const STATUS_SOLICITACAO: readonly StatusOpcao[] = [
  { id: 'aberto', rotulo: 'Aberto', icone: 'clock' },
  { id: 'em-andamento', rotulo: 'Em andamento', icone: 'rotate-ccw' },
  { id: 'respondido', rotulo: 'Respondido', icone: 'message-square' },
  { id: 'finalizado', rotulo: 'Finalizado', icone: 'check' },
  { id: 'cancelado', rotulo: 'Cancelado', icone: 'circle-x' },
];

export function formatarData(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}


/** Só letras e dígitos, em maiúsculas: `cse-2026-000001` casa com `CSE-2026-000001-K7Q4X`. */
export function normalizarProtocolo(valor: string): string {
  return valor.replace(/[^a-z0-9]/gi, '').toUpperCase();
}


@Service()
export class SolicitacoesService {
  private readonly itens = signal<readonly Solicitacao[]>([
    {
      protocolo: 'PM-2026-98754',
      servico: 'Tapa-buraco',
      descricao:
        'Solicitação de reparo asfáltico urgente devido a grande cratera aberta na via pública.',
      endereco: 'Av. ACM, Próximo ao Shopping Boulevard. Salvador-BA',
      abertaEm: '2026-05-12',
      abertaAs: '09:30',
      status: 'finalizado',
      categoria: 'ouvidoria',
      tipo: 'Reclamação',
      prazo: '30 dias úteis',
      arquivos: [
        { id: 'foto-cratera', nome: 'foto_cratera.jpg', data: '12/05/2026', tamanho: '1.9 MB', url: '#' },
      ],
      historico: [
        {
          id: 'recebida',
          rotulo: 'Recebido pela Ouvidoria',
          tom: 'azul',
          quando: '12/05/2026 - 09:30',
          descricao: 'Triagem inicial concluída.',
        },
        {
          id: 'encaminhada',
          rotulo: 'Encaminhado para SEINFRA',
          tom: 'azul',
          quando: '12/05/2026 - 11:15',
          descricao: 'Registrado e transferido com sucesso.',
        },
        {
          id: 'analise',
          rotulo: 'Em Análise Técnica',
          tom: 'ambar',
          quando: '13/05/2026 - 14:30',
          descricao: 'Sindicância de campo agendada.',
        },
        {
          id: 'concluida',
          rotulo: 'Manifestação Concluída',
          tom: 'verde',
          quando: '15/05/2026 - 10:14',
          descricao: 'Aprovado e concluído pelo setor de manutenção viária.',
        },
      ],
      resposta: {
        respondidaEm: '15/05/2026',
        paragrafos: [
          'Informamos que uma equipe técnica da Secretaria Municipal de Infraestrutura (SEINFRA) esteve no local indicado no dia 14/05/2026 e realizou a recomposição do pavimento. A via encontra-se liberada e sinalizada. Agradecemos sua colaboração cidadã.',
        ],
      },
    },
    {
      protocolo: 'PM-2026-98711',
      servico: 'Iluminação pública',
      descricao:
        'Poste com lâmpada apagada há mais de uma semana, gerando sensação de insegurança.',
      endereco: 'Rua Chile, Centro Histórico. Salvador-BA',
      abertaEm: '2026-05-10',
      abertaAs: '08:12',
      status: 'em-andamento',
      categoria: 'ouvidoria',
      tipo: 'Reclamação',
      prazo: '15 dias úteis',
      arquivos: [],
      historico: [
        {
          id: 'recebida',
          rotulo: 'Recebido pela Ouvidoria',
          tom: 'azul',
          quando: '10/05/2026 - 08:12',
          descricao: 'Triagem inicial concluída.',
        },
        {
          id: 'encaminhada',
          rotulo: 'Encaminhado para SEMOP',
          tom: 'azul',
          quando: '10/05/2026 - 10:40',
          descricao: 'Registrado e transferido com sucesso.',
        },
        {
          id: 'analise',
          rotulo: 'Em Análise Técnica',
          tom: 'ambar',
          quando: '11/05/2026 - 09:05',
          descricao: 'Sindicância de campo agendada.',
        },
      ],
    },
    {
      protocolo: 'PM-2026-98650',
      servico: 'Poda de árvore',
      descricao:
        'Árvore com galhos pesados tocando a rede elétrica e ameaçando cair sobre veículos.',
      endereco: 'Av. Paralela, Km 4 - Entrada do Imbuí. Salvador-BA',
      abertaEm: '2026-05-08',
      abertaAs: '17:47',
      status: 'aberto',
      categoria: 'servico',
      tipo: 'Solicitação de serviço',
      prazo: '30 dias úteis',
      arquivos: [],
      historico: [
        {
          id: 'recebida',
          rotulo: 'Recebido pela Ouvidoria',
          tom: 'azul',
          quando: '08/05/2026 - 17:47',
          descricao: 'Protocolo PM-2026-98650 gerado.',
        },
      ],
    },
    {
      protocolo: 'PM-2026-98622',
      servico: 'Acesso à informação',
      descricao:
        'Pedido de acesso aos contratos de manutenção viária firmados no primeiro semestre de 2026.',
      endereco: 'Atendimento eletrônico - e-SIC',
      abertaEm: '2026-05-05',
      abertaAs: '13:22',
      status: 'respondido',
      categoria: 'sic',
      tipo: 'Pedido de acesso à informação',
      prazo: '20 dias corridos',
      arquivos: [],
      historico: [
        {
          id: 'recebida',
          rotulo: 'Recebido pelo SIC',
          tom: 'azul',
          quando: '05/05/2026 - 13:22',
          descricao: 'Pedido registrado com base na Lei de Acesso à Informação.',
        },
        {
          id: 'analise',
          rotulo: 'Em Análise Técnica',
          tom: 'ambar',
          quando: '06/05/2026 - 09:10',
          descricao: 'Encaminhado à unidade detentora da informação.',
        },
        {
          id: 'respondida',
          rotulo: 'Resposta Disponibilizada',
          tom: 'verde',
          quando: '18/05/2026 - 16:05',
          descricao: 'Resposta publicada no painel do requerente.',
        },
      ],
      resposta: {
        respondidaEm: '18/05/2026',
        paragrafos: [
          'Os contratos solicitados estão publicados no Portal da Transparência, na seção Contratos e Convênios. Caso necessite de cópia integral dos anexos técnicos, o pedido pode ser complementado por este mesmo protocolo.',
        ],
      },
    },
  ]);

  readonly solicitacoes = this.itens.asReadonly();

  buscarPorProtocolo(protocolo: string): readonly Solicitacao[] {
    const procurado = normalizarProtocolo(protocolo);
    if (!procurado) return [];

    return this.itens().filter((solicitacao) =>
      normalizarProtocolo(solicitacao.protocolo).includes(procurado)
    );
  }
}
